import { User } from '@/types/User';
import {
  DIAGNOSTICS_CONSTANTS,
  SyncLogLevel,
  SyncLogOutcome,
} from '@/utils/diagnostics/diagnostics.constants';
import { sanitizeLogData } from '@/utils/diagnostics/sanitizeLogData.util';

export interface LogRequestPayload {
  message: string;
  level: SyncLogLevel;
  data: Record<string, unknown> | null;
  user: User | null;
}

export interface ChunkedLogPart {
  partId: string;
  logId: string;
  message: string;
  level: SyncLogLevel;
  data: Record<string, unknown>;
  byteSize: number;
  payload: LogRequestPayload;
}

export interface ConsolidatedSyncLogInput {
  inspectionId: string;
  syncId: string;
  sessionId: string;
  version: string;
  outcome: SyncLogOutcome;
  startedAt: string;
  finishedAt: string;
  droppedEventCount: number;
  events: Array<{
    level: SyncLogLevel;
    message: string;
    data: unknown;
    at: string;
  }>;
  user: User | null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** POST /logs accepts only a plain object in `data`. Arrays are rejected. */
export function toApiLogData(data: unknown): Record<string, unknown> | null {
  if (data == null) {
    return null;
  }
  if (Array.isArray(data)) {
    if (data.length === 1 && isPlainObject(data[0])) {
      return data[0];
    }
    return { items: data };
  }
  if (isPlainObject(data)) {
    return data;
  }
  return { value: data };
}

export function normalizeLogRequestPayload(payload: {
  message: string;
  level: SyncLogLevel;
  data: unknown;
  user: User | null;
}): LogRequestPayload {
  return {
    message: payload.message,
    level: payload.level,
    data: toApiLogData(payload.data),
    user: payload.user,
  };
}

function buildCorrelationMessage(
  baseMessage: string,
  part: number,
  total: number,
  sessionId: string,
  syncId: string
): string {
  if (total <= 1) {
    return `${baseMessage} sessionId=${sessionId} syncId=${syncId}`;
  }
  return `${baseMessage} [${part}/${total}] sessionId=${sessionId} syncId=${syncId}`;
}

function buildPartPayload(
  input: ConsolidatedSyncLogInput,
  logId: string,
  part: number,
  total: number,
  events: ConsolidatedSyncLogInput['events']
): ChunkedLogPart {
  const level: SyncLogLevel =
    input.events.some((event) => event.level === 'error') || input.outcome === 'failed'
      ? 'error'
      : 'info';
  const baseMessage =
    input.outcome === 'success'
      ? 'inspection sync complete'
      : input.outcome === 'failed'
        ? 'inspection sync failed'
        : 'inspection sync interrupted';
  const message = buildCorrelationMessage(baseMessage, part, total, input.sessionId, input.syncId);
  const data: Record<string, unknown> = {
    sessionId: input.sessionId,
    syncId: input.syncId,
    inspectionId: input.inspectionId,
    logId,
    partId: `${logId}-${part}`,
    part,
    total,
    outcome: input.outcome,
    version: input.version,
    startedAt: input.startedAt,
    finishedAt: input.finishedAt,
    droppedEventCount: input.droppedEventCount,
    eventCount: events.length,
    events: events.map((event) => ({
      level: event.level,
      message: event.message,
      data: sanitizeLogData(event.data),
      at: event.at,
    })),
  };
  const payload: LogRequestPayload = {
    message,
    level,
    data,
    user: input.user,
  };
  const byteSize = JSON.stringify(payload).length;
  return {
    partId: `${logId}-${part}`,
    logId,
    message,
    level,
    data,
    byteSize,
    payload,
  };
}

export function chunkConsolidatedSyncLog(input: ConsolidatedSyncLogInput): ChunkedLogPart[] {
  try {
    const logId = crypto.randomUUID();
    const sanitizedEvents = input.events.map((event) => ({
      ...event,
      data: sanitizeLogData(event.data),
    }));
    if (sanitizedEvents.length === 0) {
      return [buildPartPayload(input, logId, 1, 1, [])];
    }
    const eventGroups: ConsolidatedSyncLogInput['events'][] = [];
    let currentEvents: ConsolidatedSyncLogInput['events'] = [];
    for (const event of sanitizedEvents) {
      const candidateEvents = [...currentEvents, event];
      const candidateSize = buildPartPayload(input, logId, 1, 1, candidateEvents).byteSize;
      if (
        candidateSize > DIAGNOSTICS_CONSTANTS.CLIENT_SAFE_CHAR_LIMIT &&
        currentEvents.length > 0
      ) {
        eventGroups.push(currentEvents);
        currentEvents = [event];
        continue;
      }
      currentEvents = candidateEvents;
    }
    if (currentEvents.length > 0) {
      eventGroups.push(currentEvents);
    }
    const total = eventGroups.length;
    return eventGroups.map((events, index) =>
      buildPartPayload(input, logId, index + 1, total, events)
    );
  } catch {
    const logId = crypto.randomUUID();
    return [
      buildPartPayload(
        {
          ...input,
          events: [],
          droppedEventCount: input.droppedEventCount + input.events.length,
        },
        logId,
        1,
        1,
        []
      ),
    ];
  }
}

export function chunkImmediateLog(
  message: string,
  level: SyncLogLevel,
  data: unknown,
  user: User | null,
  sessionId: string | null,
  syncId: string | null
): ChunkedLogPart[] {
  try {
    const logId = crypto.randomUUID();
    const sanitized = sanitizeLogData(data);
    const correlationSuffix = [
      sessionId ? `sessionId=${sessionId}` : null,
      syncId ? `syncId=${syncId}` : null,
    ]
      .filter(Boolean)
      .join(' ');
    const baseMessage = correlationSuffix ? `${message} ${correlationSuffix}` : message;
    const payload: LogRequestPayload = {
      message: baseMessage,
      level,
      data: toApiLogData(sanitized),
      user,
    };
    const byteSize = JSON.stringify(payload).length;
    if (byteSize <= DIAGNOSTICS_CONSTANTS.CLIENT_SAFE_CHAR_LIMIT) {
      return [
        {
          partId: `${logId}-1`,
          logId,
          message: baseMessage,
          level,
          data: { sessionId, syncId, logId, partId: `${logId}-1`, part: 1, total: 1 },
          byteSize,
          payload,
        },
      ];
    }
    const serialized = JSON.stringify(sanitized);
    const chunkBodySize = DIAGNOSTICS_CONSTANTS.CLIENT_SAFE_CHAR_LIMIT - 500;
    const chunks: string[] = [];
    for (let index = 0; index < serialized.length; index += chunkBodySize) {
      chunks.push(serialized.slice(index, index + chunkBodySize));
    }
    const total = chunks.length;
    return chunks.map((chunk, index) => {
      const part = index + 1;
      const partMessage =
        total <= 1 ? baseMessage : `${message} [${part}/${total}] ${correlationSuffix}`.trim();
      const partPayload: LogRequestPayload = {
        message: partMessage,
        level,
        data: {
          sessionId,
          syncId,
          logId,
          partId: `${logId}-${part}`,
          part,
          total,
          chunk,
        },
        user,
      };
      return {
        partId: `${logId}-${part}`,
        logId,
        message: partMessage,
        level,
        data: { sessionId, syncId, logId, partId: `${logId}-${part}`, part, total },
        byteSize: JSON.stringify(partPayload).length,
        payload: partPayload,
      };
    });
  } catch {
    const logId = crypto.randomUUID();
    const fallbackMessage = `${message} sessionId=${sessionId ?? 'unknown'} syncId=${syncId ?? 'unknown'}`;
    const payload: LogRequestPayload = {
      message: fallbackMessage,
      level,
      data: { sessionId, syncId, logId, part: 1, total: 1, error: 'chunk-failed' },
      user,
    };
    return [
      {
        partId: `${logId}-1`,
        logId,
        message: fallbackMessage,
        level,
        data: { sessionId, syncId, logId, partId: `${logId}-1`, part: 1, total: 1 },
        byteSize: JSON.stringify(payload).length,
        payload,
      },
    ];
  }
}
